import React, { useCallback, useEffect } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import clsx from 'clsx';

// Komponen & Hooks Shared
import FormSpinner from '../../../shared/components/spinner/FormSpinner';

// Hooks, Schemas, dan Tipe untuk V4
import { useGetUserV4 } from '../hooks/useGetUserV4';
import { useUpdateUserV4 } from '../hooks/useUpdateUserV4';
import { UpdateUserFormData, UpdateUserV4Schema } from '../schemas/UpdateUserV4Schema';
import ErrorState from './ErrorState';

/**
 * Props untuk komponen UpdateUserDialogV4.
 * @param show - Boolean untuk mengontrol visibilitas dialog.
 * @param onClose - Callback function yang dipanggil saat dialog ditutup.
 * @param userId - ID dari pengguna yang akan diperbarui.
 */
interface UpdateUserDialogV4Props {
    show: boolean;
    onClose: () => void;
    userId: string;
}

/**
 * Komponen dialog (Offcanvas) untuk memperbarui data pengguna (Versi 4).
 * Mengambil data pengguna yang ada, mengisi formulir, dan menangani logika pembaruan.
 */
const UpdateUserDialogV4: React.FC<UpdateUserDialogV4Props> = ({ show, onClose, userId }) => {
    // 1. Mengambil data pengguna yang ada menggunakan useQuery
    const {
        data: userResponse,
        isLoading: isLoadingUser,
        isError: isErrorLoading,
        error: loadingError,
        refetch,
    } = useGetUserV4(userId);

    // 2. Setup React Hook Form
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
        setError
    } = useForm<UpdateUserFormData>({
        resolver: zodResolver(UpdateUserV4Schema),
        defaultValues: {
            name: '',
            email: '',
            phone: '',
            password: '', // Password sengaja kosong by default
        }
    });

    // 3. Mengisi form dengan data dari API setelah berhasil diambil
    useEffect(() => {
        if (userResponse?.data) {
            const user = userResponse.data;
            reset({
                name: user.name,
                email: user.email,
                phone: user.phone || '', // Pastikan phone tidak null
                password: '', // Selalu kosongkan password saat memuat
            });
        }
    }, [userResponse, reset]);

    // 4. Hook mutasi untuk memperbarui pengguna
    const { mutate: updateUser } = useUpdateUserV4();

    // Handler untuk menutup dialog, dengan me-reset form
    const handleClose = () => {
        reset();
        onClose();
    };

    // 5. Handler untuk submit form
    const onSubmit = useCallback((formData: UpdateUserFormData) => {
        const toastId = 'update-user-v4';
        toast.loading('Updating user...', { id: toastId });

        updateUser({ userId, formData }, {
            onSuccess: () => {
                toast.success('User updated successfully.', { id: toastId });
                handleClose();
            },
            onError: (error) => {
                const errorResponse = (error as any)?.response?.data;

                if (errorResponse?.code === 422 && errorResponse?.errors) {
                    toast.error('Please check the form for errors.', { id: toastId });
                    for (const [field, messages] of Object.entries(errorResponse.errors)) {
                        setError(field as keyof UpdateUserFormData, {
                            type: 'server',
                            message: (messages as string[])[0]
                        });
                    }
                } else {
                    const errorMessage = errorResponse?.message || 'An unexpected error occurred.';
                    toast.error(errorMessage, { id: toastId });
                }
            }
        });
    }, [updateUser, userId, handleClose, setError]);

    // 6. Render Logic untuk body Offcanvas
    const renderBody = () => {
        if (isLoadingUser) {
            return <FormSpinner />;
        }

        if (isErrorLoading) {
            return <ErrorState error={loadingError} onRetry={refetch} />;
        }

        if (userResponse) {
            return (
                <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column h-100">
                    <div className="flex-grow-1">
                        {/* Name Field */}
                        <div className="mb-3">
                            <label htmlFor="update-name-v4" className="form-label">Full Name</label>
                            <input
                                id="update-name-v4"
                                type="text"
                                className={clsx("form-control", { 'is-invalid': errors.name })}
                                {...register('name')}
                                disabled={isSubmitting}
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                        </div>

                        {/* Email Field */}
                        <div className="mb-3">
                            <label htmlFor="update-email-v4" className="form-label">Email Address</label>
                            <input
                                id="update-email-v4"
                                type="email"
                                className={clsx("form-control", { 'is-invalid': errors.email })}
                                {...register('email')}
                                disabled={isSubmitting}
                            />
                            {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
                        </div>

                        {/* Phone Field */}
                        <div className="mb-3">
                            <label htmlFor="update-phone-v4" className="form-label">Phone Number</label>
                            <input
                                id="update-phone-v4"
                                type="text"
                                className={clsx("form-control", { 'is-invalid': errors.phone })}
                                {...register('phone')}
                                disabled={isSubmitting}
                            />
                            {errors.phone && <div className="invalid-feedback">{errors.phone.message}</div>}
                        </div>

                        {/* Password Field */}
                        <div className="mb-3">
                            <label htmlFor="update-password-v4" className="form-label">New Password</label>
                            <input
                                id="update-password-v4"
                                type="password"
                                className={clsx("form-control", { 'is-invalid': errors.password })}
                                {...register('password')}
                                placeholder="Leave blank to keep current password"
                                disabled={isSubmitting}
                            />
                            {errors.password && <div className="invalid-feedback">{errors.password.message}</div>}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-auto d-flex justify-content-end gap-2">
                        <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={isSubmitting}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                            {isSubmitting ? 'Updating...' : 'Update User'}
                        </button>
                    </div>
                </form>
            );
        }

        return null; // Fallback
    };

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end" backdrop="static" className="offcanvas-responsive">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Update User</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                {renderBody()}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default UpdateUserDialogV4;